import React, { useState } from 'react';
import InfoTip from '../shared/InfoTip';
import AnswerCard from './answers/AnswerCard';
import ClosedAndNew from './ClosedAndNewPanel';
import { CARD_LIST, CARD_TITLE, PILL } from './liStyles';

// Right now — what to suggest before anyone has typed anything: the meal for this
// hour and something to do today, three answers each. A weather reason, when there
// is one, shows as a chip (this page never shows weather itself).
const RightNow = ({ panel, openNowCount, onOpen, onAskNeed, onBook, onOutcome, closedNowPanel, onOpenDrill }) => {
  const blocks = Array.isArray(panel?.facts?.blocks) ? panel.facts.blocks : [];
  // Item 1 — occasion tabs. Only one block's cards show at a time, so the panel
  // height stays fixed regardless of how many occasion blocks the API returns.
  // Default to the first block (index 0). Clamp the index if blocks shrink so a
  // stale active tab never points past the end of the array.
  const [activeTab, setActiveTab] = useState(0);
  const safeIndex = blocks.length === 0 ? 0 : Math.min(activeTab, blocks.length - 1);
  const activeBlock = blocks[safeIndex];
  return (
    <section id="panel-right_now" className={CARD_LIST} aria-label="Open right now">
      {/* Header mirrors HelpRail's (same padding, border-b, and element order) so the
          two panels' titles and subtexts align horizontally when they sit side by
          side. The title is shortened to one line so it carries the same visual
          weight as "Essential Services Nearby"; the reason chip sits inline beside it. */}
      <div className="px-4 sm:px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-800">
        <h2 className={`${CARD_TITLE} text-lg`}>
          Places Open Right Now
          <InfoTip text="Suggestions for this hour, before a guest asks for anything specific." />
        </h2>
        {panel?.facts?.reason_label && <span className={`${PILL.gray} ml-2`}>{panel.facts.reason_label}</span>}
        {/* Item 2 — the open-now sublabel moved here from What does the guest need. The
            count comes from the open_now panel's open_within_20_min field (passed in as
            openNowCount by the page), not hardcoded. Same small muted styling and
            position as the HelpRail subtext so the two panels' subtext lines align. */}
        {openNowCount != null && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-2">{openNowCount} places within a 20-minute drive are open right now.</p>
        )}
      </div>
      {/* Item 1 — redundant headline removed. The top-level `panel.sentence` repeated
          the first block's label context ("For tonight: ...", "Still open late: ..."),
          so it duplicated the occasion the tab bar now communicates. The title and
          sublabel above are the only panel-level text. */}
      {blocks.length > 0 && (
        <div className="p-4 sm:p-5">
          {/* Tab bar — one tab per occasion block. Browser-style tabs (not pills):
              a horizontal bar with a baseline border; the active tab is underlined in
              brand blue with full-color text, inactive tabs are muted with a
              transparent border so they recede. Clicking a tab switches the active
              block. */}
          <div className="flex gap-1 border-b border-gray-200 dark:border-gray-800 mb-4" role="tablist">
            {blocks.map((block, i) => (
              <button
                type="button"
                key={block.need_key || block.label}
                role="tab"
                aria-selected={i === safeIndex}
                onClick={() => setActiveTab(i)}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  i === safeIndex
                    ? 'border-[#32B1FF] text-gray-900 dark:text-gray-100'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {/* Occasion-first, matching the original stacked-block section label:
                    `reason_label` is the occasion phrasing ("For tonight", "Clear
                    evening", "Still open late"); fall back to the need-category
                    `label` ("Dinner", "Sunset spot") when no occasion string exists. */}
                {block.reason_label || block.label}
              </button>
            ))}
          </div>
          {/* Only the active block's cards render. The per-block section label
              ("STILL OPEN LATE", "CLEAR EVENING") is gone — the active tab already
              communicates the occasion. Cards use the existing AnswerCard design
              (headline, meta chips, READY TO SEND GUESTS badge + hours-verified
              text, action buttons on one line with a divider) — no card redesign. */}
          {Array.isArray(activeBlock.answers) && activeBlock.answers.length > 0 ? (
            <div className="space-y-2">
              {activeBlock.answers.map((a, i) => (
                <AnswerCard key={a.entity_id} answer={a} index={i} onOpen={onOpen} onBook={onBook} onOutcome={onOutcome} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nothing nearby fits this hour yet.</p>
          )}
        </div>
      )}
      {/* Closed right now — slim footer strip embedded in this card (work-page-fixes
          Item 1). The button shows only the label and the combined count; clicking
          opens a right-hand slide-out with the closed/new-on-map lists. Only renders
          when the page passes a changes panel in (not when it's salient — that case
          renders standalone in the attention slot). */}
      {closedNowPanel && (
        <ClosedAndNew variant="footer" panel={closedNowPanel} onOpenDrill={onOpenDrill} />
      )}
    </section>
  );
};

export default RightNow;
